<template>
    <AppModal
        title="Account Verification Required"
        v-model="open"
    >
        <template #icon>
            <FingerPrintIcon
                class="h-6 w-6 "
                :class="scopeState.claimed ? 'text-green-600' : (scopeState.error ? 'text-red-600' : 'text-blue-600')"
            />
        </template>
        <p>
            We have emailed the account owner a six-digit code. Please enter it below to verify your account.
        </p>
        <form
            class="flex flex-col gap-4 mt-4"
            @submit.prevent="handleScopeClaim"
        >
            <label for="code" class="block text-sm font-medium text-gray-700">
                Verification Code
            </label>
            <input
                type="text"
                name="code"
                id="code"
                autocomplete="one-time-code"
                v-model="scopeState.code"
            />
            <div
                class="flex justify-between gap-4"
            >
                <button
                    type="button"
                    class="button button--secondary"
                    :disabled="!scopeState.canResendCode || scopeState.loading"
                    @click="handleSendCode"
                >
                    Resend
                </button>
                <button
                    type="submit"
                    class="button button--primary"
                    :disabled="scopeState.claimed || scopeState.code.length !== 6 || scopeState.loading"
                >
                    Verify
                </button>
            </div>
            <AppLoading v-if="scopeState.loading" />
        </form>
    </AppModal>
</template>

<script setup lang="ts">
import { useUserStore } from '@/store/user'
import { reactive, computed, onMounted } from 'vue'
import { FingerPrintIcon } from '@heroicons/vue/24/outline'
import AppModal from '@/components/App/AppModal.vue'
import AppLoading from '@/components/App/AppLoading.vue'

interface Props {
    modelValue: boolean;
    scopes: string[];
}

const props = defineProps<Props>()
const emit = defineEmits(['update:modelValue', 'scopes'])
const userStore = useUserStore()

const scopeState = reactive({
    showScopeClaimModal: false,
    code: '',
    scope: 'sensitive',
    claimed: false,
    error: null as string | null,
    loading: false,
    hasSentCode: false,
    canResendCode: false,
    challenge: null as AuthScopeChallenge|null,
    result: null as ClaimScopeChallengeResult|null,
})

onMounted(() => {
    handleSendCode()
})

const open = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
})


const handleSendCode = async() => {
    scopeState.loading = true
    try {
        const response = await userStore.claimScope({
            scopes: props.scopes,
        })
        scopeState.challenge = response?.challenge || null
        scopeState.result = response?.result || null
        setTimeout(() => {
            scopeState.canResendCode = true
        }, 10000)
    } catch (e: any) {
        console.error(e)
    } finally {
        scopeState.loading = false
    }
}

const handleScopeClaim = async() => {
    scopeState.loading = true
    try {
        const response = await userStore.claimScope({
            scopes: props.scopes,
            code: scopeState.code,
            session: scopeState.challenge?.session,
        })
        scopeState.challenge = response?.challenge || null
        scopeState.result = response?.result || null

        if (!response?.challenge && response?.result) {
            // no challenge required & result has session data
            scopeState.claimed = true
            userStore.$patch({
                scopes: response.result.scopes,
            })
            userStore.setSession(response.result)
            userStore.saveState()
            emit('scopes', response.result.scopes)
            setTimeout(() => {
                open.value = false
            }, 1200)
        }

        // @TODO: handle challenge
    } catch (e: any) {
        console.error(e)
        scopeState.error = e?.message || e || 'Invalid code'
    } finally {
        scopeState.loading = false
    }
}

</script>
